// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@adl/App/Components/Notify";
import submitSpecialRequestItems from "@adl/App/Handlers/SpecialRequestItems/SendSpecialRequestItems";
import { CustomAppState } from "@adl/App/Store/Reducers";
import {
    initialSpecialRequestItemsFormState,
    SpecialRequestItemsFormState,
} from "@adl/App/Widgets/SpecialRequestItems/SpecialRequestItems.form.module";
import { AccordionSectionPresentationProps } from "@adl/MobiusOverrides/AccordionSection";
import { color, xIsAuthenticated } from "@adl/XDL/Utilities";
import { CreateForm } from "@adl/XDL/Utilities/form";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { SelectPresentationProps } from "@insite/mobius/Select/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import parse from "html-react-parser";

const enum fields {
    title = "title",
    details = "details",
}

interface OwnProps {
    fields: {
        [fields.title]: string;
        [fields.details]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    submitSpecialRequestItems,
};

type Props = OwnProps & WidgetModule & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SpecialRequestItemsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    formWrapper?: InjectableCss;
    detailsWrapper?: InjectableCss;
    textField?: TextFieldPresentationProps;
    textArea?: TextAreaPresentationProps;
    selectField?: SelectPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

const specialRequestItemsStyles: SpecialRequestItemsStyles = {
    formWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            > div,
            > .notify {
                margin-bottom: 15px;
            }
        `,
    },
    textArea: {
        mergeCss: true,
        css: css`
            max-height: 160px;
            resize: vertical;
        `,
    },
    accordion: {
        css: css`
            border: 1px solid ${color("common.border")};
        `,
    },
    accordionSection: {
        mergeCss: true,
        headerProps: {
            css: css`
                &&& button {
                    padding: 12px 16px;
                    background: ${color("common.ui9")};
                    border: none;
                }
            `,
        },
        panelProps: {
            css: css`
                background: ${color("common.white")};
                padding: 12px 16px 16px;
                border: none;
            `,
        },
        titleTypographyProps: {
            variant: "h6",
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    detailsWrapper: {
        css: css`
            margin-bottom: 16px;
        `,
    },
};

const styles = specialRequestItemsStyles;

const SpecialRequestItems: FC<Props> = ({ fields: { title, details }, isAuthenticated, submitSpecialRequestItems }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toaster = useToaster();

    const [specialRequestItemsFormState, setSpecialRequestItemsFormState] = useState<SpecialRequestItemsFormState>(
        cloneDeep(initialSpecialRequestItemsFormState),
    );
    const specialRequestItemsForm = new CreateForm<SpecialRequestItemsFormState>(
        specialRequestItemsFormState,
        setSpecialRequestItemsFormState,
    );

    const onSubmitHandler = () => {
        const { isSubmitted } = specialRequestItemsForm.state;

        if (!isSubmitted) {
            specialRequestItemsForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        if (!specialRequestItemsForm.validate()) {
            return;
        }

        setIsSubmitting(true);
        specialRequestItemsForm.resetForm(initialSpecialRequestItemsFormState);
        setErrorMessage("");

        const formState: { [key: string]: string } = {};
        Object.keys(specialRequestItemsForm.state.fields).forEach(key => {
            formState[key] = (specialRequestItemsForm.state.fields as any)?.[key].value;
        });

        submitSpecialRequestItems({
            ...formState,
            onSuccess: () => {
                setIsSubmitting(false);
                setErrorMessage("");
                toaster.addToast({
                    body: siteMessage("Custom_SpecialRequestItemsSentSuccessfully"),
                    messageType: "success",
                });
            },
            onError: errorMessage => {
                setIsSubmitting(false);
                setErrorMessage(errorMessage);
            },
        });
    };

    if (!isAuthenticated) {
        return null;
    }

    const { fields, isSubmitted } = specialRequestItemsForm.state;
    const isFormValid = specialRequestItemsForm.validate();

    return (
        <Accordion headingLevel={2} {...styles.accordion}>
            <ManagedAccordionSection title={title} initialExpanded={false} {...styles.accordionSection}>
                <StyledWrapper {...styles.detailsWrapper}>{parse(details, parserOptions)}</StyledWrapper>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        value={fields.partNumber.value}
                        onChange={e => specialRequestItemsForm?.onFieldChange(fields.partNumber, e.currentTarget.value)}
                        error={isSubmitted && fields.partNumber.error}
                        {...fields.partNumber.attrs}
                        {...styles.textField}
                    />
                    <TextField
                        value={fields.qtyNeeded.value}
                        onChange={e => {
                            const value = e.currentTarget.value;
                            const valid = /^[1-9][0-9]*$/.test(value);
                            if (!valid && value !== "") {
                                return;
                            }
                            specialRequestItemsForm?.onFieldChange(fields.qtyNeeded, value);
                        }}
                        error={isSubmitted && fields.qtyNeeded.error}
                        {...fields.qtyNeeded.attrs}
                        {...styles.textField}
                    />
                    <TextArea
                        value={fields.description.value}
                        onChange={e =>
                            specialRequestItemsForm?.onFieldChange(fields.description, e.currentTarget.value)
                        }
                        error={isSubmitted && fields.description.error}
                        {...fields.description.attrs}
                        {...styles.textArea}
                    />

                    {errorMessage && <Notify type="danger">{errorMessage}</Notify>}
                    <Button
                        onClick={onSubmitHandler}
                        disabled={isSubmitting || (!isFormValid && specialRequestItemsForm.state.isSubmitted)}
                    >
                        {isSubmitting ? <LoadingSpinner size={12} /> : translate("Send")}
                    </Button>
                </StyledWrapper>
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SpecialRequestItems),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Add Non-Stock Products",
            },
            {
                name: fields.details,
                editorTemplate: "RichTextField",
                fieldType: "General",
                defaultValue:
                    "<p><strong>Can&#39;t find what you need?</strong>&nbsp;\n<br>We can get it for you. Add items onto your order even if you can&#39;t find them online.\n<br>We will find it and confirm with you as we add it to the order.</p>",
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
